import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useData } from '../../contexts/DataContext';
import { useHospitalBranding } from '../../contexts/HospitalBrandingContext';
import { useHospitalAuth } from '../../contexts/HospitalAuthContext';
import { apiRequest } from '../../config/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import ChangePassword from '../../components/doctor/ChangePassword';
import PatientRecords from '../../components/patient/PatientRecords';
import BookAppointment from '../../components/patient/BookAppointment';
import { 
  FileText, 
  Calendar, 
  User, 
  Upload, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  LogOut,
  Stethoscope,
  Phone,
  Mail,
  Key
} from 'lucide-react';

const PatientDashboard = () => {
  const { profile, signOut, loading: authLoading } = useAuth();
  const { submissions } = useData();
  const { branding } = useHospitalBranding();
  const { hospital } = useHospitalAuth();
  const navigate = useNavigate();
  const [showRecords, setShowRecords] = useState(false);
  const [showBookAppointment, setShowBookAppointment] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [hospitalAddress, setHospitalAddress] = useState(null);

  // Verify user is actually a patient
  // IMPORTANT: Only check after auth loading is complete to prevent redirects during session restore
  React.useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) {
      return; // Still loading, don't check yet
    }
    
    // If loading is complete and no profile, ProtectedRoute will handle redirect
    if (!profile) {
      return; // Let ProtectedRoute handle the redirect
    }
    
    // Now check if the profile is actually a patient
    const userRole = profile?.role?.toLowerCase();
    const isPatient = userRole === 'patient' || profile?.patient_unique_id || profile?.type === 'patient';
    
    if (!isPatient) {
      console.warn('PatientDashboard: User is not a patient, redirecting. Role:', userRole, 'Type:', profile?.type);
      toast({
        title: 'Access Denied',
        description: 'This dashboard is only available for patients. Please log in as a patient.',
        variant: 'destructive'
      });
      // Redirect based on role
      if (userRole === 'clinic_admin' || userRole === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (userRole === 'doctor' || userRole === 'chief_doctor') {
        navigate('/doctor/dashboard', { replace: true });
      } else {
        navigate('/patient/login', { replace: true });
      }
    }
  }, [profile, authLoading, navigate]);

  const userSubmissions = submissions.filter(sub => sub.patient_id === profile?.id);
  const recentSubmission = userSubmissions.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0];

  // Fetch appointments for the patient
  useEffect(() => {
    if (profile?.id) {
      fetchAppointments();
    }
    
    // Listen for appointment creation events
    const handleAppointmentCreated = () => {
      fetchAppointments();
    };
    
    window.addEventListener('appointment-created', handleAppointmentCreated);
    return () => {
      window.removeEventListener('appointment-created', handleAppointmentCreated);
    };
  }, [profile?.id]);

  // Update current date/time every second (IST)
  useEffect(() => {
    const updateDateTime = () => {
      setCurrentDateTime(new Date());
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Fetch hospital address if not available in branding/hospital context
  useEffect(() => {
    const fetchHospitalAddress = async () => {
      // If address is already available from branding or hospital context, use it
      if (branding?.address || hospital?.address) {
        setHospitalAddress(branding?.address || hospital?.address);
        return;
      }

      // Otherwise, try to fetch from profile's hospital_id
      if (profile?.hospital_id) {
        try {
          const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${profile.hospital_id}`);
          if (statusData.success && statusData.hospital?.address) {
            setHospitalAddress(statusData.hospital.address);
          }
        } catch (error) {
          console.error('Error fetching hospital address:', error);
        }
      }
    };

    fetchHospitalAddress();
  }, [profile?.hospital_id, branding?.address, hospital?.address]);

  const fetchAppointments = async () => {
    setLoadingAppointments(true);
    try {
      // Fetch scheduled and submitted appointments
      const scheduledData = await apiRequest('get_appointments_list.php?status=Scheduled');
      const submittedData = await apiRequest('get_appointments_list.php?status=Submitted');
      
      const allAppointments = [
        ...(scheduledData.success ? scheduledData.appointments || [] : []),
        ...(submittedData.success ? submittedData.appointments || [] : [])
      ];
      
      // Sort by date (newest first)
      allAppointments.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date} ${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date} ${b.appointment_time}`);
        return dateB - dateA;
      });
      
      setAppointments(allAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleLogout = async () => {
    // IMPORTANT: Get hospital slug BEFORE clearing session/state
    // Once signOut() is called, branding and hospital contexts will be cleared
    let hospitalSlug = branding?.slug || hospital?.slug;
    
    // If slug is not available but we have hospital_id in profile, try to fetch it
    if (!hospitalSlug && profile?.hospital_id) {
      try {
        const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${profile.hospital_id}`);
        if (statusData.success && statusData.hospital?.slug) {
          hospitalSlug = statusData.hospital.slug;
        }
      } catch (error) {
        console.error('Error fetching hospital slug:', error);
      }
    }
    
    // Also try to get slug from localStorage hospital_data as a fallback (BEFORE signOut clears it)
    if (!hospitalSlug) {
      try {
        const storedHospital = localStorage.getItem('hospital_data');
        if (storedHospital) {
          const hospitalData = JSON.parse(storedHospital);
          hospitalSlug = hospitalData.slug;
        }
      } catch (error) {
        console.error('Error parsing hospital_data from localStorage:', error);
      }
    }
    
    try {
      // Clear any local state before signOut
      setShowRecords(false);
      setShowBookAppointment(false);
      
      // Now sign out (this will clear context and localStorage)
      await signOut();
      
      // Redirect to hospital landing page if slug is available, otherwise to dental hub landing page
      // Use setTimeout to ensure navigation happens after state updates
      setTimeout(() => {
        if (hospitalSlug) {
          navigate(`/${hospitalSlug}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 100);
      
      toast({
        title: 'Logged out successfully',
        description: 'See you next time!'
      });
    } catch (error) {
      console.error('Logout error:', error);
      
      // Even if logout API fails, redirect with the slug we already captured
      setShowRecords(false);
      setShowBookAppointment(false);
      
      // Redirect
      setTimeout(() => {
        if (hospitalSlug) {
          navigate(`/${hospitalSlug}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 100);
    }
  };

  const quickActions = [
    {
      title: 'Fill Health Form',
      description: 'Complete your dental health questionnaire',
      icon: FileText,
      action: () => navigate('/patient/form'),
      color: 'bg-blue-500'
    },
    {
      title: 'Upload Files',
      description: 'Upload X-rays, reports, or images',
      icon: Upload,
      action: () => toast({
        title: '🚧 This feature isn\'t implemented yet— 🚀'
      }),
      color: 'bg-green-500'
    },
    {
      title: 'Book Appointment',
      description: 'Schedule your next dental visit',
      icon: Calendar,
      action: () => setShowBookAppointment(true),
      color: 'bg-purple-500'
    },
    {
      title: 'View Records',
      description: 'Access your medical history',
      icon: User,
      action: () => setShowRecords(true),
      color: 'bg-orange-500'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-yellow-400" />;
      default:
        return <AlertCircle className="h-5 w-5 text-blue-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'in-progress':
        return 'text-yellow-400';
      default:
        return 'text-blue-400';
    }
  };

  return (
    <>
      <Helmet>
        <title>Patient Dashboard - {branding?.name || 'Dental Hub'}</title>
        <meta name="description" content="Access your dental health records, book appointments, and manage your dental care from your patient dashboard." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                {branding?.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt={`${branding.name || 'Hospital'} Logo`}
                    className="h-10 md:h-12 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`${branding?.logoUrl ? 'hidden' : 'flex'} items-center justify-center h-10 w-10 md:h-12 md:w-12 bg-blue-600 rounded-lg`}>
                  <Stethoscope className="h-6 w-6 md:h-8 md:w-8 text-white" />
                </div>
                <span className="hidden md:block text-xl font-bold gradient-text">
                  {branding?.name || 'Dental Hub'}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-white text-sm sm:text-base">Welcome, {profile?.full_name || profile?.name}</span>
                  {profile?.patient_unique_id && (
                    <span className="text-gray-400 text-xs mt-0.5">Patient ID: {profile.patient_unique_id}</span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-red-400 p-2"
                  size="sm"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-2">
                  Patient Dashboard
                </h1>
                <p className="text-gray-300 text-lg">
                  Manage your dental health and appointments
                </p>
              </div>
              <div className="text-right">
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Current Date & Time (IST)</p>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    {currentDateTime.toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                  <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                    {currentDateTime.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickActions.map((action, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className="glass-effect border-gray-700 card-hover cursor-pointer h-full"
                    onClick={action.action}
                  >
                    <CardHeader className="pb-3">
                      <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center mb-3`}>
                        <action.icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-white text-lg">{action.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-300">
                        {action.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="glass-effect border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-blue-400" />
                    Your Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingAppointments ? (
                    <div className="flex items-center justify-center py-8">
                      <Clock className="h-8 w-8 text-blue-400 animate-spin mr-2" />
                      <span className="text-gray-300">Loading appointments...</span>
                    </div>
                  ) : appointments.length > 0 || userSubmissions.length > 0 ? (
                    <div className="space-y-4">
                      {/* Show appointments first */}
                      {appointments.slice(0, 5).map((appointment) => {
                        // Check if appointment was booked by patient
                        // If creator_type is 'patient' or creator_patient_name exists, it's patient-booked
                        const isPatientBooked = appointment.creator_type === 'patient' || 
                                                (appointment.created_by === profile?.id && appointment.creator_patient_name) ||
                                                appointment.creator_patient_name;
                        const appointmentDate = new Date(`${appointment.appointment_date} ${appointment.appointment_time}`);
                        const isPast = appointmentDate < new Date();
                        
                        return (
                          <div 
                            key={`appointment-${appointment.id}`} 
                            className={`flex flex-col p-3 rounded-lg ${
                              isPatientBooked 
                                ? 'bg-blue-900/30 border border-blue-500/50' 
                                : 'bg-gray-800'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <Calendar className={`h-5 w-5 ${
                                  appointment.status === 'Submitted' ? 'text-green-400' : 
                                  isPast ? 'text-gray-400' : 'text-blue-400'
                                }`} />
                                <div>
                                  <p className="text-white font-medium">
                                    {appointment.appointment_reference_id || `Appointment #${appointment.id}`}
                                  </p>
                                  <p className="text-gray-400 text-sm">
                                    {appointment.doctor_name ? `Dr. ${appointment.doctor_name}` : 'Doctor TBD'}
                                  </p>
                                </div>
                              </div>
                              <span className={`text-xs font-medium px-2 py-1 rounded ${
                                appointment.status === 'Submitted' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {appointment.status}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <div className="text-gray-300">
                                <p>{appointmentDate.toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}</p>
                                <p className="text-gray-400">{appointmentDate.toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}</p>
                              </div>
                              {isPatientBooked && (
                                <span className="text-blue-300 text-xs font-medium">
                                  Booked by: {appointment.creator_name || appointment.creator_patient_name || profile?.full_name || 'You'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Show submitted records */}
                      {userSubmissions.filter(sub => sub.status === 'completed' || sub.status === 'submitted').slice(0, 3).map((submission) => (
                        <div key={`submission-${submission.id}`} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-400" />
                            <div>
                              <p className="text-white font-medium">Record Submitted</p>
                              <p className="text-gray-400 text-sm">
                                {new Date(submission.submitted_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm font-medium text-green-400">
                            Completed
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-400">No appointments scheduled</p>
                      <p className="text-gray-500 text-sm">Book an appointment to get started</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Profile Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card className="glass-effect border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <User className="h-5 w-5 mr-2 text-blue-400" />
                    Profile Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-white font-medium">{profile?.full_name || profile?.name}</p>
                      <p className="text-gray-400 text-sm">Patient</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-white">{profile?.email}</p>
                      <p className="text-gray-400 text-sm">Email Address</p>
                    </div>
                  </div>
                  
                  {profile?.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-white">{profile.phone}</p>
                        <p className="text-gray-400 text-sm">Phone Number</p>
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                    onClick={() => toast({
                      title: '🚧 This feature isn\'t implemented yet— 🚀'
                    })}
                  >
                    Edit Profile
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Password Change/Reset Card */}
            {profile?.can_reset_password && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card className="glass-effect border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <Key className="h-5 w-5 mr-2 text-blue-400" />
                      Change/Reset Password
                    </CardTitle>
                    <CardDescription className="text-gray-300">
                      Change your password or reset it to the default password
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ChangePassword />
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Recent Submission Details */}
          {recentSubmission && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8"
            >
              <Card className="glass-effect border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-400" />
                    Latest Submission
                  </CardTitle>
                  <CardDescription className="text-gray-300">
                    Submitted on {new Date(recentSubmission.submitted_at).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Chief Complaint</p>
                      <p className="text-white">{recentSubmission.chief_complaint || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Pain Level</p>
                      <p className="text-white">{recentSubmission.pain_level || 'Not specified'}/10</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Status</p>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(recentSubmission.status)}
                        <span className={`font-medium ${getStatusColor(recentSubmission.status)}`}>
                          {recentSubmission.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Assigned Doctor</p>
                      <p className="text-white">{recentSubmission.doctors?.name || 'Not assigned'}</p>
                    </div>
                  </div>
                  
                  {recentSubmission.doctor_feedback && (
                    <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                      <p className="text-gray-400 text-sm mb-2">Doctor's Feedback</p>
                      <p className="text-white">{recentSubmission.doctor_feedback}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto">
            {/* Clinic Contact Information */}
            <div className={`grid grid-cols-1 md:grid-cols-2 ${branding?.address || hospital?.address ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6 mb-6`}>
              {/* Contact */}
              <div className="text-center md:text-left">
                <h3 className="text-white font-semibold mb-3 flex items-center justify-center md:justify-start">
                  <Phone className="h-4 w-4 mr-2 text-blue-400" />
                  Contact
                </h3>
                <p className="text-gray-300 text-sm mb-1">{branding?.phone || hospital?.phone || '+91 96299 96774'}</p>
                <p className="text-gray-300 text-sm flex items-center justify-center md:justify-start">
                  <Mail className="h-3 w-3 mr-1 text-gray-400" />
                  {branding?.email || hospital?.email || 'Contact your clinic'}
                </p>
              </div>

              {/* Hospital Address */}
              {hospitalAddress || branding?.address || hospital?.address ? (
                <div className="text-center md:text-left">
                  <h3 className="text-white font-semibold mb-3">Address</h3>
                  <p className="text-gray-300 text-sm whitespace-pre-line">
                    {hospitalAddress || branding?.address || hospital?.address}
                  </p>
                </div>
              ) : (
                <>
                  {/* Fallback: Main Branch (only shown if no address in database) */}
                  <div className="text-center md:text-left">
                    <h3 className="text-white font-semibold mb-3">Main Branch</h3>
                    <p className="text-gray-300 text-sm">
                      GRK Complex, Bawa Medical Upstairs,<br />
                      Near Bustand Signal,<br />
                      Gobichettipalayam – 638452
                    </p>
                  </div>

                  {/* Fallback: Other Location (only shown if no address in database) */}
                  <div className="text-center md:text-left">
                    <h3 className="text-white font-semibold mb-3">Other Location</h3>
                    <p className="text-gray-300 text-sm">
                      Vairam Bricks Complex, Vasthu Nagar,<br />
                      Saravana Theatre Rd,<br />
                      Gobichettipalayam – 638476
                    </p>
                  </div>
                </>
              )}

              {/* Logo and Name */}
              <div className="flex flex-col items-center md:items-start">
                <div className="flex items-center space-x-2 mb-3">
                  {branding?.logoUrl ? (
                    <img 
                      src={branding.logoUrl} 
                      alt={`${branding.name || 'Hospital'} Logo`}
                      className="h-8 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`${branding?.logoUrl ? 'hidden' : 'flex'} items-center justify-center h-8 w-8 bg-blue-600 rounded-lg`}>
                    <Stethoscope className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-lg font-semibold gradient-text">
                    {branding?.name || 'Dental Hub'}
                  </span>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                © 2025 Dental Hub. All rights reserved. Developed and maintained by{' '}
                <a 
                  href="https://www.neinus.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                >
                  Neinus Technologies
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Patient Records Modal */}
      <PatientRecords
        patientId={profile?.id}
        isOpen={showRecords}
        onClose={() => setShowRecords(false)}
      />

      {/* Book Appointment Modal */}
      <BookAppointment
        isOpen={showBookAppointment}
        onClose={() => setShowBookAppointment(false)}
        patientId={profile?.id}
      />
    </>
  );
};

export default PatientDashboard;